import { useCallback, useState } from 'react';
import { virusBlocksActions } from '@citrite/citrix-files-ui';
import { Account, isFile, Item, User } from '@citrite/sf-api';
import { TextFile } from '@sharefiledev/icons';
import type {
	FolderItemContextActionAvailableParams,
	PiletApi,
} from '@sharefiledev/sharefile-appshell';
import { useAppStore } from '../../common/store';
import { DocGenExtensionSlots, WorkflowType } from '../../constants';
import { onDocumentTemplateClose } from '../../pages/CreateDocumentTemplate';
import { t } from '../../utils';
import { isCreateTemplateActionAvailable } from '../../utils/access';
import { getFileExtension, isDocxFile, isSupportedFileFormat } from '../../utils/file';
import { CreateDocumentTemplateModal } from '../CreateDocumentTemplateModal';

export interface PiletUserContext {
	user: User;
	account: Account;
}

interface CreateTemplateProps {
	selectedItem: Item;
	ActionButton: any;
	extensionName: string;
	redirectPath: string;
	actionButton?: Function;
	ActionComponent?: any;
	name?: string;
	app: PiletApi;
}

interface CreateTemplateContextMenuProps
	extends Omit<CreateTemplateProps, 'selectedItem'> {
	selectedItems?: Item[];
}

export const openCreateTemplateDrawer = app => {
	app.showDrawer(DocGenExtensionSlots.CREATE_DOCUMENT_TEMPLATE_DRAWER, {
		onCancelInteraction: () => {
			return onDocumentTemplateClose();
		},
	});
};

export function CreateTemplateButton(params: CreateTemplateProps) {
	const { updateTemplateMetadata } = useAppStore.getState();

	const [isModalOpen, setModalOpen] = useState(false);

	const handleClickFromCV = useCallback(() => {
		if (isModalOpen) {
			return;
		}

		updateTemplateMetadata({
			selectedItem: params.selectedItem,
			workflow: WorkflowType.ContentViewer,
		});

		if (isDocxFile(params.selectedItem.FileName)) {
			setModalOpen(true);
		} else {
			openCreateTemplateDrawer(params.app);
		}
	}, [params.selectedItem, params.app, updateTemplateMetadata, isModalOpen]);

	const handleClickFromDashboard = () => {
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
	};

	let ActionButton;

	if (params.extensionName === 'urn:sfextslot:sharefile-appshell:dashboard:shortcuts') {
		const actionButtonProps = {
			onClick: handleClickFromDashboard,
			icon: ({ size = 24, ...rest }) => <TextFile size={size} {...rest} />,
		};

		ActionButton = () => (
			<>
				<params.ActionButton {...actionButtonProps}>
					{t('extensionSlots.dashboardShortcut')}
				</params.ActionButton>
				{isModalOpen && (
					<CreateDocumentTemplateModal app={params.app} onClose={handleModalClose} />
				)}
			</>
		);
	} else if (
		params.extensionName === 'urn:sfextslot:sharefile-contentviewer:context-actions'
	) {
		const isSupported = isSupportedFileFormat(
			getFileExtension(params.selectedItem.FileName)
		);

		const actionButtonProps = {
			onClick: handleClickFromCV,
			icon: ({ size = 24, ...rest }) => <TextFile size={size} {...rest} />,
		};

		ActionButton = () =>
			isSupported && (
				<>
					<params.ActionComponent {...actionButtonProps}>
						{t('extensionSlots.createNewTemplate')}
					</params.ActionComponent>
					{isModalOpen && (
						<CreateDocumentTemplateModal app={params.app} onClose={handleModalClose} />
					)}
				</>
			);
	}

	return ActionButton ? <ActionButton /> : null;
}

export function CreateTemplateContextMenuButton(params: CreateTemplateContextMenuProps) {
	const { updateTemplateMetadata } = useAppStore(state => ({
		updateTemplateMetadata: state.updateTemplateMetadata,
		resetStore: state.resetStore,
	}));

	const [isModalOpen, setModalOpen] = useState(false);

	const handleClickFromContextMenu = useCallback(() => {
		if (isModalOpen) {
			return;
		}

		updateTemplateMetadata({
			selectedItem: params.selectedItems?.[0],
			workflow: WorkflowType.ContextMenu,
		});

		if (isDocxFile(params.selectedItems?.[0]?.FileName)) {
			setModalOpen(true);
		} else {
			openCreateTemplateDrawer(params.app);
		}
	}, [params.selectedItems, params.app, updateTemplateMetadata, isModalOpen]);

	const handleModalClose = () => {
		setModalOpen(false);
	};

	const actionButtonProps = {
		onClick: handleClickFromContextMenu,
		icon: ({ size = 20, ...rest }) => <TextFile size={size} {...rest} />,
	};

	const ActionButton = () => {
		return (
			<>
				<params.ActionComponent {...actionButtonProps}>
					{t('extensionSlots.createNewTemplate')}
				</params.ActionComponent>
				{isModalOpen && (
					<CreateDocumentTemplateModal app={params.app} onClose={handleModalClose} />
				)}
			</>
		);
	};

	return <ActionButton />;
}

function isAvailable(
	{ selectedItems, managingUserContent }: FolderItemContextActionAvailableParams,
	account: Account
) {
	if (!managingUserContent && selectedItems.length === 1) {
		const [item] = selectedItems;

		const isSupported = isSupportedFileFormat(getFileExtension(item.FileName));

		return (
			isFile(item) &&
			isSupported &&
			Boolean(item.ItemOperations) &&
			!virusBlocksActions(item, account.Preferences!)
		);
	}
	return false;
}

export function CreateTemplateSlot(
	app: PiletApi,
	extensionName: string,
	piletSortWeight?: number
) {
	const { account, entitlements } = useAppStore.getState();

	if (isCreateTemplateActionAvailable(account, entitlements)) {
		app.registerExtension(
			extensionName,
			props => {
				const { params } = props;
				if (
					extensionName === 'urn:sfextslot:sharefile-appshell:folder:item-context-actions'
				) {
					return (
						<CreateTemplateContextMenuButton
							{...params}
							extensionName={extensionName}
							app={app}
						/>
					);
				} else {
					return (
						<CreateTemplateButton {...params} extensionName={extensionName} app={app} />
					);
				}
			},
			{
				piletSortWeight: piletSortWeight ? piletSortWeight : undefined,
				isAvailable: params => isAvailable(params, account),
			}
		);
	} else {
		return;
	}
}
